import { toolsState } from '@core/components';
import type { ThreeEvent } from '@react-three/fiber';
import { Object3dNames } from '@viewer3D/types';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { Group, Mesh, Vector3 } from 'three';
import { proxy, useSnapshot } from 'valtio';

type MeasurementPoints = Partial<{ from: Vector3; to: Vector3 }>;
const measurementPoints = proxy<MeasurementPoints>({});
const setMeasurementPoints = ({ from, to }: MeasurementPoints): void => {
  measurementPoints.from = from;
  measurementPoints.to = to;
};

interface UseMeasurementProps {
  invalidate: () => void;
}

interface UseMeasurement {
  divRef: RefObject<HTMLDivElement | null>;
  groupRef: RefObject<Group | null>;
  isActive: boolean;
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  onOver: (event: ThreeEvent<MouseEvent>) => void;
}

export const useMeasurement = ({ invalidate }: UseMeasurementProps): UseMeasurement => {
  const { measurementActive } = useSnapshot(toolsState);

  const groupRef = useRef<Group>(null);
  const divRef = useRef<HTMLDivElement>(null);

  const alignMeasurementLine = ({ stopPropagation, point }: Partial<ThreeEvent<MouseEvent>>) => {
    stopPropagation?.();
    // eslint-disable-next-line prefer-const
    let { from, to } = measurementPoints;
    if (!divRef.current || !groupRef.current || !from || to || !point) return;
    to = point;

    // Setup Threejs Group
    const midPoint = from.clone().add(to).divideScalar(2);
    groupRef.current.position.copy(midPoint);

    // Build Line
    const line = groupRef.current.children.find(
      (c) => c.name === Object3dNames.MeasurementLine,
    ) as Mesh;

    if (!line) return;

    const distance = from.distanceTo(to);
    const direction = new Vector3().subVectors(to, from).normalize();

    line.position.set(0, 0, 0);
    line.scale.set(1, distance, 1);
    line.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), direction);

    // Build div measurement content
    const distanceZ = new Vector3()
      .copy(new Vector3(0, 0, from.z))
      .distanceTo(new Vector3(0, 0, to.z));
    const distanceInCm = Math.round(distance * 100);
    const distanceZInCm = Math.round(distanceZ * 100);
    divRef.current.innerText = `${distanceInCm} cm | Z:${distanceZInCm} cm`;
    invalidate();
  };

  const alignRef = useRef(alignMeasurementLine);
  useEffect(() => {
    alignRef.current = alignMeasurementLine;
  });

  const overThrottleRef = useRef<{
    inThrottle: boolean;
    lastArgs: { point: Vector3 } | null;
    timeoutId: number | null;
  }>({
    inThrottle: false,
    lastArgs: null,
    timeoutId: null,
  });

  const throttledOnOver = useMemo(() => {
    const throttled = (args: { point: Vector3 }) => {
      const state = overThrottleRef.current;
      if (!state.inThrottle) {
        alignRef.current(args);
        state.inThrottle = true;
        state.timeoutId = window.setTimeout(() => {
          state.inThrottle = false;
          if (state.lastArgs) {
            const nextArgs = state.lastArgs;
            state.lastArgs = null;
            throttled(nextArgs);
          }
        }, 30);
      } else {
        state.lastArgs = args;
      }
    };

    const cancel = () => {
      const state = overThrottleRef.current;
      if (state.timeoutId !== null) {
        window.clearTimeout(state.timeoutId);
        state.timeoutId = null;
      }
      state.inThrottle = false;
      state.lastArgs = null;
    };

    return { throttled, cancel };
  }, []);

  useEffect(() => {
    return () => {
      throttledOnOver.cancel();
    };
  }, [throttledOnOver]);

  const onClick = useCallback(
    ({ intersections, point, stopPropagation }: ThreeEvent<MouseEvent>) => {
      stopPropagation();
      if (!intersections.length) return;
      const { from, to } = measurementPoints;
      if (!from || (from && to)) {
        setMeasurementPoints({ from: point });
        alignRef.current({ point });
      } else {
        setMeasurementPoints({ from, to: point });
      }
    },
    [],
  );

  useEffect(() => {
    if (measurementActive) return;
    setMeasurementPoints({});
  }, [measurementActive]);

  const onOver = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();
      throttledOnOver.throttled({ point: event.point });
    },
    [throttledOnOver],
  );

  return { isActive: measurementActive, groupRef, divRef, onClick, onOver };
};
